import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;
const EMPTY = { companyName: '', contactPerson: '', email: '', phone: '', website: '', city: '', state: '', country: '' };

export default function DashboardCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const { getIdToken }            = useAuth();

  useEffect(() => { fetchCompanies(); }, []);

  function fetchCompanies() {
    const token = getIdToken();
    axios.get(`${API_URL}/api/companies.json`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setCompanies(res.data.companies || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }

  function openAdd() { setForm({ ...EMPTY }); setEditingId(null); setError(''); setSuccess(''); setShowForm(true); }

  function openEdit(c) {
    setForm({
      companyName: c.companyName || '', contactPerson: c.contactPerson || '',
      email: c.email || '', phone: c.phone || '', website: c.website || '',
      city: c.city || '', state: c.state || '', country: c.country || '',
    });
    setEditingId(c.id); setError(''); setSuccess(''); setShowForm(true);
  }

  function cancel() { setShowForm(false); setEditingId(null); setForm({ ...EMPTY }); setError(''); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    const token = getIdToken();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/companies/edit.json?id=${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Company updated.');
      } else {
        await axios.post(`${API_URL}/api/companies.json`, form, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Company added.');
      }
      setShowForm(false); setEditingId(null); setForm({ ...EMPTY }); fetchCompanies();
    } catch { setError('Failed to save company.'); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this company?')) return;
    const token = getIdToken();
    try {
      await axios.delete(`${API_URL}/api/companies/remove.json?id=${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch { setError('Failed to delete company.'); }
  }

  return (
    <div>
      <div style={s.topBar}>
        <h2 style={s.pageTitle}>Manage Companies</h2>
        {!showForm && <button onClick={openAdd} style={s.addBtn}>+ Add Company</button>}
      </div>

      {success && <div style={s.success}>{success}</div>}
      {error   && <div style={s.error}>{error}</div>}

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>{editingId ? 'Edit Company' : 'Add New Company'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={s.twoCol}>
              <Field label="Company Name"><input style={s.input} type="text" value={form.companyName} onChange={set('companyName')} placeholder="Acme Corp" /></Field>
              <Field label="Website"><input style={s.input} type="url" value={form.website} onChange={set('website')} placeholder="https://company.com" /></Field>
            </div>
            <div style={s.twoCol}>
              <Field label="Contact Person"><input style={s.input} type="text" value={form.contactPerson} onChange={set('contactPerson')} placeholder="Full name" /></Field>
              <Field label="Email"><input style={s.input} type="email" value={form.email} onChange={set('email')} placeholder="press@company.com" /></Field>
            </div>
            <div style={s.twoCol}>
              <Field label="Phone"><input style={s.input} type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" /></Field>
              <Field label="City"><input style={s.input} type="text" value={form.city} onChange={set('city')} placeholder="New York" /></Field>
            </div>
            <div style={s.twoCol}>
              <Field label="State"><input style={s.input} type="text" value={form.state} onChange={set('state')} placeholder="NY" /></Field>
              <Field label="Country"><input style={s.input} type="text" value={form.country} onChange={set('country')} placeholder="USA" /></Field>
            </div>
            <div style={s.formActions}>
              <button type="button" onClick={cancel} style={s.btnOutline}>Cancel</button>
              <button type="submit" disabled={saving} style={s.btnPrimary}>
                {saving ? 'Saving...' : editingId ? 'Update Company' : 'Add Company'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : companies.length === 0 && !showForm ? (
        <div style={s.empty}>
          <p>No companies yet.</p>
          <button onClick={openAdd} style={s.btnPrimary}>Add your first company</button>
        </div>
      ) : (
        <div style={s.grid}>
          {companies.map((c) => (
            <div key={c.id} style={s.card}>
              <div style={s.cardHeader}>
                <h3 style={s.cardName}>{c.companyName || 'Unnamed Company'}</h3>
                <div style={s.cardActions}>
                  <button onClick={() => openEdit(c)} style={s.editBtn}>Edit</button>
                  <button onClick={() => handleDelete(c.id)} style={s.deleteBtn}>Delete</button>
                </div>
              </div>
              <div style={s.cardDetails}>
                {c.contactPerson && <span style={s.detail}>👤 {c.contactPerson}</span>}
                {c.email && <span style={s.detail}>✉️ {c.email}</span>}
                {c.phone && <span style={s.detail}>📞 {c.phone}</span>}
                {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" style={s.detailLink}>🌐 {c.website.replace(/^https?:\/\//, '')}</a>}
                {(c.city || c.country) && <span style={s.detail}>📍 {[c.city, c.state, c.country].filter(Boolean).join(', ')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#444', marginBottom: '0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' },
  pageTitle: { margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e' },
  addBtn: { padding: '0.5rem 1.1rem', background: '#1a62d5', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' },
  success: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#15803d', fontSize: '0.9rem' },
  error: { background: '#fee', border: '1px solid #fcc', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#c00', fontSize: '0.9rem' },
  loading: { textAlign: 'center', padding: '3rem', color: '#888' },
  empty: { textAlign: 'center', padding: '3rem', color: '#888', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formCard: { background: '#fff', borderRadius: 10, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '2rem', border: '2px solid #1a62d5' },
  formTitle: { margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  input: { width: '100%', padding: '0.6rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  formActions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  btnPrimary: { padding: '0.65rem 1.4rem', background: '#1a62d5', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { padding: '0.65rem 1.4rem', background: 'transparent', color: '#555', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1.25rem' },
  card: { background: '#fff', borderRadius: 8, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', borderTop: '3px solid #1a62d5' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' },
  cardName: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' },
  cardActions: { display: 'flex', gap: '0.5rem' },
  editBtn: { padding: '0.25rem 0.65rem', background: '#f0f0f0', color: '#444', border: 'none', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 },
  deleteBtn: { padding: '0.25rem 0.65rem', background: '#fff0f0', color: '#dc2626', border: 'none', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 },
  cardDetails: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  detail: { fontSize: '0.82rem', color: '#555' },
  detailLink: { fontSize: '0.82rem', color: '#1a62d5', textDecoration: 'none' },
};
